<template>
  <div class="hpap-Wrapper">
    <SkeletonItem v-if="isEntityLoading" :appearance="entityAppearance" :size="XL" width="48px" />
    <template v-else-if="entity">
      <GroupIcon
        v-if="isGroup"
        :color="entity[GLOBAL_GROUP_KEYS.COLOR]"
        :icon-name="entity[GLOBAL_GROUP_KEYS.ICON]"
        :icon-size="48"
      />
      <AppAvatar
        v-else
        :avatar-url="entity[ASSIGNEE_ENTITY_KEYS.AVATAR_URL]"
        :size="AVATAR_SIZES.XL"
        no-margins
      />
    </template>

    <template v-if="isEntityLoading">
      <SkeletonItem height="28px" width="180px" />
    </template>
    <div
      v-else-if="entity"
      :data-auto-testid="HOMEPAGE_AVERAGE_PROGRESS_NAME_TEST_ID"
      :data-testid="HOMEPAGE_AVERAGE_PROGRESS_NAME_TEST_ID"
      class="hpap-Name oboard-truncated-text"
    >
      {{ entityName }}
    </div>

    <div class="hpap-Figures">
      <template v-if="isAlignmentLoading">
        <template v-for="figure in FIGURE_SKELETONS" :key="figure">
          <AppDivider vertical />
          <!-- SkeletonItem renders through a functional child that drops data attributes,
               so the test id sits on a wrapper of its own -->
          <div
            :data-auto-testid="HOMEPAGE_AVERAGE_PROGRESS_FIGURES_LOADER_TEST_ID"
            :data-testid="HOMEPAGE_AVERAGE_PROGRESS_FIGURES_LOADER_TEST_ID"
            class="hpap-FigureLoader"
          >
            <SkeletonItem height="50px" width="120px" />
          </div>
        </template>
      </template>

      <template v-else>
        <AppDivider vertical />
        <button
          :class="{ 'hpap-Figure-clickable': isAlignmentClickable }"
          :data-auto-testid="HOMEPAGE_ALIGNMENT_FIGURE_TEST_ID"
          :data-testid="HOMEPAGE_ALIGNMENT_FIGURE_TEST_ID"
          :disabled="!isAlignmentClickable"
          class="hpap-Figure"
          type="button"
          @click="onAlignmentFigureClick"
        >
          <div class="hpap-Figure_Value">
            <span
              :class="{
                'hpap-Figure_Number-score': hasAlignmentScore,
                'hpap-Figure_Number-empty': !hasAlignmentScore
              }"
              :data-auto-testid="HOMEPAGE_ALIGNMENT_FIGURE_VALUE_TEST_ID"
              :data-testid="HOMEPAGE_ALIGNMENT_FIGURE_VALUE_TEST_ID"
              class="hpap-Figure_Number"
            >
              {{ alignmentFigureText }}
            </span>
            <span
              v-if="hasAlignmentScore"
              :data-auto-testid="HOMEPAGE_ALIGNMENT_FIGURE_BAR_TEST_ID"
              :data-testid="HOMEPAGE_ALIGNMENT_FIGURE_BAR_TEST_ID"
              class="hpap-Figure_Bar"
            >
              <span :style="{ width: alignmentBarWidth }" class="hpap-Figure_BarValue" />
            </span>
          </div>
          <div class="hpap-Figure_Caption">
            <AppIcon
              :height="FIGURE_ICON_SIZE"
              :width="FIGURE_ICON_SIZE"
              class="hpap-Figure_Icon"
              icon-name="magic-wand"
            />
            {{ t('homepage.ai_reports.figure.alignment') }}
          </div>
        </button>

        <AppDivider vertical />
        <div
          :data-auto-testid="HOMEPAGE_ENGAGEMENT_FIGURE_TEST_ID"
          :data-testid="HOMEPAGE_ENGAGEMENT_FIGURE_TEST_ID"
          class="hpap-Figure"
        >
          <div class="hpap-Figure_Value">
            <span
              :data-auto-testid="HOMEPAGE_ENGAGEMENT_FIGURE_VALUE_TEST_ID"
              :data-testid="HOMEPAGE_ENGAGEMENT_FIGURE_VALUE_TEST_ID"
              class="hpap-Figure_Number hpap-Figure_Number-empty"
            >
              {{ AI_REPORT_FIGURE_EMPTY_VALUE }}
            </span>
          </div>
          <div class="hpap-Figure_Caption">
            <AppIcon
              :height="FIGURE_ICON_SIZE"
              :width="FIGURE_ICON_SIZE"
              class="hpap-Figure_Icon"
              icon-name="magic-wand"
            />
            {{ t('homepage.ai_reports.figure.engagement') }}
          </div>
        </div>

        <AppButtonNext
          v-if="canGenerateAlignment"
          v-tippy="{ content: t('homepage.ai_reports.debug.open'), placement: 'top' }"
          :aria-label="t('homepage.ai_reports.debug.open')"
          :data-auto-testid="HOMEPAGE_AI_REPORT_DEBUG_BUTTON_TEST_ID"
          :data-testid="HOMEPAGE_AI_REPORT_DEBUG_BUTTON_TEST_ID"
          :size="BUTTON_SIZES_NEXT.SM"
          :variant="BUTTON_VARIANTS.TERTIARY_GRAY"
          icon-before="bug"
          @click="isDebugDialogOpen = true"
        />
      </template>
    </div>

    <div class="hpap-Progress">
      <SkeletonItem v-if="isProgressLoading" height="50px" width="78px" />
      <template v-else>
        <AppDivider vertical />
        <div class="hpap-ProgressText">
          <div
            :data-auto-testid="HOMEPAGE_AVERAGE_PROGRESS_VALUE_TEST_ID"
            :data-testid="HOMEPAGE_AVERAGE_PROGRESS_VALUE_TEST_ID"
            class="hpap-ProgressValue"
          >
            <AnimatedNumber
              v-if="simplifiedProgress"
              v-slot="{ displayValue }"
              :decimals="progressDecimals"
              :end-amount="simplifiedProgress[PLURALIZATION_KEYS.FLOAT_NUMBER]"
            >
              {{ formatProgress(displayValue) }}
            </AnimatedNumber>
            <template v-else>—</template>
          </div>
          <div class="hpap-ProgressCaption">
            {{ $t('dashboard.avg_progress') }}
          </div>
        </div>
      </template>
    </div>

    <HomePageAlignmentReportDialog
      v-model:show="isAlignmentDialogOpen"
      :interval-name="alignmentFilterNames.intervalName"
      :is-generating="isAlignmentGenerating"
      :nothing-to-analyse="isAlignmentNothingToAnalyse"
      :report-section="alignmentSection"
      :selection-name="selectionName"
      :workspace-name="alignmentFilterNames.workspaceName"
      @recalculate="emit('generate-alignment')"
    />

    <HomePageAiReportDebugDialog
      v-model:show="isDebugDialogOpen"
      :account-id="debugSelection.accountId"
      :group-id="debugSelection.groupId"
      :selection-name="selectionName"
    />
  </div>
</template>

<script setup>
import axios from 'axios'
import { clamp, isNil, isNull } from 'lodash'
import { computed, inject, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'

import HomePageApiHandler from '@/api/homepage'
import HomepageGroupsApiHandler from '@/api/homepage-groups'
import HomepageUsersApiHandler from '@/api/homepage-users'
import { AVATAR_SIZES } from '@/utils/components-configurations/app-avatar'
import { BUTTON_SIZES_NEXT, BUTTON_VARIANTS } from '@/utils/components-configurations/app-button'
import {
  SKELETON_APPEARANCES,
  SKELETON_SIZES
} from '@/utils/components-configurations/skeleton-item'
import { ASSIGNEE_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { GLOBAL_GROUP_KEYS } from '@/utils/global-groups'
import {
  AI_REPORT_FIGURE_EMPTY_VALUE,
  AI_REPORT_KEYS,
  AI_REPORT_RESPONSE_KEYS
} from '@/utils/home-page/ai-report'
import { getAiReportFilterNames } from '@/utils/home-page/ai-report-filters'
import { getAvailableCustomRangeParams } from '@/utils/homepage'
import { homePageStateInjectionKey } from '@/utils/injection-keys'
import { UNSELECTED_INTERVAL } from '@/utils/interval'
import {
  isTimeRangeSelectedInsteadOfInterval,
  NEW_HP_FILTERS_KEYS
} from '@/utils/okr-elements/filters'
import { MATH_SYMBOLS } from '@/utils/okr-elements/math'
import { getSimplifiedNumber, PLURALIZATION_KEYS } from '@/utils/pluralization'
import { objectOrNullProp } from '@/utils/prop-validators'
import { useLoadingState } from '@/utils/use-loading-state'

import GroupIcon from '@/components/global-groups/GroupIcon'
import HomePageAiReportDebugDialog from '@/components/home-page/HomePageAiReportDebugDialog'
import HomePageAlignmentReportDialog from '@/components/home-page/HomePageAlignmentReportDialog'
import {
  HOMEPAGE_AI_REPORT_DEBUG_BUTTON_TEST_ID,
  HOMEPAGE_ALIGNMENT_FIGURE_BAR_TEST_ID,
  HOMEPAGE_ALIGNMENT_FIGURE_TEST_ID,
  HOMEPAGE_ALIGNMENT_FIGURE_VALUE_TEST_ID,
  HOMEPAGE_AVERAGE_PROGRESS_FIGURES_LOADER_TEST_ID,
  HOMEPAGE_AVERAGE_PROGRESS_NAME_TEST_ID,
  HOMEPAGE_AVERAGE_PROGRESS_VALUE_TEST_ID,
  HOMEPAGE_ENGAGEMENT_FIGURE_TEST_ID,
  HOMEPAGE_ENGAGEMENT_FIGURE_VALUE_TEST_ID
} from '@/components/home-page/jest-helpers'
import AnimatedNumber from '@/components/ui/AnimatedNumber/AnimatedNumber'
import AppAvatar from '@/components/ui/AppAvatar/AppAvatar'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppDivider from '@/components/ui/AppDivider'
import AppIcon from '@/components/ui/AppIcon/AppIcon'
import SkeletonItem from '@/components/ui/SkeletonLoaders/SkeletonItem'

defineOptions({
  name: 'HomePageAverageProgress'
})

const props = defineProps({
  // the Alignment section of the kept reports read by HomePageAiReports, null while none is served
  alignmentSection: {
    validator: v => objectOrNullProp(v),
    default: null
  },

  isAlignmentLoading: {
    type: Boolean
  },

  // false while the Interval filter is a custom range of dates: the figure opens nothing (FR-3.4)
  isAlignmentAvailable: {
    type: Boolean,
    default: true
  },

  isAlignmentGenerating: {
    type: Boolean
  },

  isAlignmentNothingToAnalyse: {
    type: Boolean
  }
})

const emit = defineEmits({
  'generate-alignment': null
})

const { t } = useI18n()

const { XL } = SKELETON_SIZES
const { CIRCLE, SQUARE } = SKELETON_APPEARANCES

// the "Alignment" and "Engagement" figures show the card's skeleton while the reports load (FR-0.32)
const FIGURE_SKELETONS = ['alignment', 'engagement']

const FIGURE_ICON_SIZE = 16

const store = useStore()

const homePageState = inject(homePageStateInjectionKey)
const homePageFilters = homePageState.filterValues
const areFiltersValuesResolved = homePageState.areFiltersValuesResolved

const groupsApi = new HomepageGroupsApiHandler()
const usersApi = new HomepageUsersApiHandler()
const homepageApi = new HomePageApiHandler()

const { isLoading: isProgressLoading, toggleLoading: toggleProgressLoading } = useLoadingState()
const { isLoading: isEntityLoading, toggleLoading: toggleEntityLoading } = useLoadingState()

const entity = ref(null)
const entityName = computed(() => {
  return isGroup.value
    ? entity.value[GLOBAL_GROUP_KEYS.NAME]
    : entity.value[ASSIGNEE_ENTITY_KEYS.NAME]
})
const entityAppearance = computed(() => {
  return isGroup.value ? SQUARE : CIRCLE
})
const progress = ref(null)

const selectedUserId = computed(() => homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_USER])
const selectedGroupId = computed(() => homePageFilters.value[NEW_HP_FILTERS_KEYS.SELECTED_GROUP])
const selectedWorkspaceIds = computed(
  () => homePageFilters.value[NEW_HP_FILTERS_KEYS.WORKSPACE_IDS]
)
const selectedGradeTypes = computed(() => homePageFilters.value[NEW_HP_FILTERS_KEYS.GRADE_TYPES])
const selectedIntervalIds = computed(() => homePageState.selectedIntervalIds.value)
const selectedDates = computed(() => {
  return {
    startDates: homePageFilters.value[NEW_HP_FILTERS_KEYS.START_DATES],
    dueDates: homePageFilters.value[NEW_HP_FILTERS_KEYS.DUE_DATES]
  }
})

const isGroup = computed(() => isNull(selectedUserId.value) && !isNull(selectedGroupId.value))

const simplifiedProgress = computed(() => {
  if (isNil(progress.value)) {
    return null
  }

  return getSimplifiedNumber(progress.value)
})

const progressDecimals = computed(() => {
  const floatNumber = simplifiedProgress.value?.[PLURALIZATION_KEYS.FLOAT_NUMBER]

  if (floatNumber == null) {
    return 0
  }

  const [, fraction = ''] = String(floatNumber).split('.')

  return fraction.length
})

const formatProgress = displayValue => {
  return [
    displayValue,
    simplifiedProgress.value[PLURALIZATION_KEYS.SYMBOL],
    MATH_SYMBOLS.PERCENT
  ].join('')
}

//#region alignment figure and dialog
const alignmentReport = computed(() => {
  return props.alignmentSection?.[AI_REPORT_RESPONSE_KEYS.REPORT] ?? null
})

const hasAlignmentReport = computed(() => !isNull(alignmentReport.value))

const canGenerateAlignment = computed(() => {
  return Boolean(props.alignmentSection?.[AI_REPORT_RESPONSE_KEYS.CAN_GENERATE])
})

// the overall alignment score of the kept report as a percentage, whatever the Home filters (FR-1.24)
const alignmentScorePercent = computed(() => {
  const percent = alignmentReport.value?.[AI_REPORT_KEYS.OVERALL_SCORE_PERCENT]

  return isNil(percent) ? null : clamp(percent, 0, 100)
})

const hasAlignmentScore = computed(() => !isNull(alignmentScorePercent.value))

const alignmentFigureText = computed(() => {
  return hasAlignmentScore.value
    ? `${alignmentScorePercent.value}${MATH_SYMBOLS.PERCENT}`
    : AI_REPORT_FIGURE_EMPTY_VALUE
})

const alignmentBarWidth = computed(() => `${alignmentScorePercent.value}${MATH_SYMBOLS.PERCENT}`)

// no report of the selection is served to the reader, or the Interval filter is a custom range:
// the figure shows no report and opens nothing (FR-0.19, FR-0.20, FR-3.4)
const isAlignmentClickable = computed(() => {
  return props.isAlignmentAvailable && !isNull(props.alignmentSection)
})

const isAlignmentDialogOpen = ref(false)

const selectionName = computed(() => (isNull(entity.value) ? '' : entityName.value))

// the names of the Interval and Workspace filters for the line of FR-0.27, which the dialog composes
const alignmentFilterNames = computed(() => {
  const { intervalLabelKey, workspaceName } = getAiReportFilterNames({
    intervalTypeId: homePageFilters.value[NEW_HP_FILTERS_KEYS.INTERVAL_TYPE_ID],
    workspaceIds: selectedWorkspaceIds.value,
    workspaces: store.state.workspaces.workspaces
  })

  return {
    intervalName: intervalLabelKey ? t(intervalLabelKey) : '',
    workspaceName: workspaceName ?? t('workspaces.workspaces')
  }
})

// the first click of a selection without a kept report starts the generation for a user with
// generate access; every click opens the dialog (FR-0.4, FR-0.5, FR-0.18)
const onAlignmentFigureClick = () => {
  if (!isAlignmentClickable.value) {
    return
  }

  if (!hasAlignmentReport.value && canGenerateAlignment.value && !props.isAlignmentGenerating) {
    emit('generate-alignment')
  }

  isAlignmentDialogOpen.value = true
}

// the debug icon belongs to a user who may generate the Alignment report (FR-0.6, FR-0.12)
const isDebugDialogOpen = ref(false)

const debugSelection = computed(() => {
  if (isGroup.value) {
    return { groupId: selectedGroupId.value, accountId: null }
  }

  return { groupId: null, accountId: selectedUserId.value }
})

// the dialogs belong to the selection they were opened for
watch([selectedUserId, selectedGroupId], () => {
  isAlignmentDialogOpen.value = false
  isDebugDialogOpen.value = false
})
//#endregion

const loadAverageProgress = ({ accountId = null, groupId = null } = {}) => {
  const customRangeOptionsBasedOnFilters = isTimeRangeSelectedInsteadOfInterval({
    ...selectedDates.value,
    intervals: selectedIntervalIds.value
  })

  return homepageApi.getAverageProgress({
    accountId,
    groupId,
    workspaceIds: selectedWorkspaceIds.value,
    gradeTypes: selectedGradeTypes.value,
    intervalIds: customRangeOptionsBasedOnFilters.isTimeRangeSelected
      ? UNSELECTED_INTERVAL
      : selectedIntervalIds.value,
    ...getAvailableCustomRangeParams({
      selectedDates: selectedDates.value,
      intervalIds: selectedIntervalIds.value
    })
  })
}

const loadProgressValue = async params => {
  const progress = await loadAverageProgress(params)
  return progress.avgProgress
}

const loadSelectedUser = async accountId => {
  try {
    toggleEntityLoading(true)
    const user = await usersApi.getUserById(accountId)
    return isNil(user) ? null : user
  } catch (error) {
    handleError({ error })
    return null
  }
}

const loadSelectedGroup = async groupId => {
  try {
    toggleEntityLoading(true)
    const group = await groupsApi.getGroupById(groupId)
    return isNil(group) ? null : group
  } catch (error) {
    handleError({ error })
    return null
  }
}

const loadAvgProgress = async ({ loadEntity } = { loadEntity: true }) => {
  if (!areFiltersValuesResolved.value) {
    return
  }

  const accountId = selectedUserId.value
  const groupId = selectedGroupId.value

  toggleProgressLoading(true)

  try {
    if (isNil(accountId) && isNil(groupId)) {
      entity.value = null
      progress.value = null
      return
    }

    let identityRequest
    let progressRequest

    if (isGroup.value) {
      identityRequest = loadEntity ? loadSelectedGroup(groupId) : Promise.resolve(null)
      progressRequest = loadProgressValue({ groupId })
    } else {
      identityRequest = loadEntity ? loadSelectedUser(accountId) : Promise.resolve(null)
      progressRequest = loadProgressValue({ accountId })
    }

    const [loadedEntity, loadedProgress] = await Promise.all([identityRequest, progressRequest])

    if (loadedEntity) {
      entity.value = loadedEntity
    }
    progress.value = loadedProgress
  } catch (error) {
    handleError({ error })

    if (!axios.isCancel(error)) {
      entity.value = null
      progress.value = null
    }
  } finally {
    toggleProgressLoading(false)
    toggleEntityLoading(false)
  }
}

onMounted(() => {
  loadAvgProgress()
})

defineExpose({
  loadAvgProgress
})
</script>

<style lang="scss" scoped>
.hpap-Wrapper {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
  border-radius: $border-radius-md-next;
  border: 1px solid $grey-2-next;
  background-color: $grey-3;
}

.hpap-Name {
  font-size: $fs-18;
  font-style: normal;
  font-weight: fw('bold');
  line-height: 28px;
  width: 100%;
}

.hpap-Figures {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.hpap-FigureLoader {
  display: flex;
  flex-shrink: 0;
}

.hpap-Figure {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  margin: 0;
  padding: 0;
  border: none;
  background: none;
  color: inherit;
  font: inherit;
  text-align: center;
  cursor: default;

  &-clickable {
    cursor: pointer;
    border-radius: $border-radius-sm-next;
    transition: background-color $transition-fast;

    &:hover,
    &:focus-visible {
      background-color: rgba($primary-color-next, 0.06);
    }

    &:focus-visible {
      outline: 2px solid $primary-color-next;
      outline-offset: 2px;
    }
  }
}

.hpap-Figure_Value {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
}

.hpap-Figure_Number {
  font-size: $fs-24;
  font-weight: fw('bold');
  line-height: 32px;

  // the score of a kept report is blue, the dash of a figure without a report is light grey,
  // whether or not the figure opens the dialog (screens 1.1.1 and 1.1.4)
  &-score {
    color: $primary-color-next;
  }

  &-empty {
    color: $grey-1-next;
  }
}

.hpap-Figure_Bar {
  display: block;
  width: 64px;
  height: 6px;
  border-radius: 3px;
  background-color: $azure-dark;
  overflow: hidden;
}

.hpap-Figure_BarValue {
  display: block;
  height: 100%;
  border-radius: inherit;
  background-color: $primary-color-next;
  transition: width $transition-normal;
}

.hpap-Figure_Caption {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--gray-700);
  font-size: $fs-12;
  font-weight: fw('regular');
  line-height: 18px;
}

.hpap-Figure_Icon {
  flex-shrink: 0;
  color: $blue-9;
}

.hpap-Progress {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}

.hpap-ProgressText {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.hpap-ProgressValue {
  font-size: $fs-24;
  font-weight: fw('bold');
  line-height: 32px;
}

.hpap-ProgressCaption {
  color: var(--gray-700);
  font-size: $fs-12;
  font-weight: fw('regular');
  line-height: 18px;
}
</style>
